/* eslint-disable react/button-has-type */
import React, { useState, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import { X } from 'lucide-react';
import {
  Elements,
  useStripe,
  useElements,
  PaymentElement,
} from '@stripe/react-stripe-js';
// eslint-disable-next-line import/no-cycle
import { finalizingCateringOrderAction } from 'redux/actions/paymentActions';
import { useHistory } from 'react-router-dom';
import LoadingAnimation from 'components/common/LoadingAnimation';
import { store } from 'react-notifications-component';
import { useDispatch } from 'react-redux';
import FormSelect from 'components/common/FormSelect';
import { UPDATE_SAVED_CARD_IN_CATEGORING_ORDER } from 'redux/catering/actionTypes';
import { getStripePromise } from 'utils/stripe.util';

// Any Stripe bank-debit payment method type this app supports saving -
// ACSS Debit (Canada), ACH/us_bank_account (US), Bacs Debit (UK). Filtering
// saved cards down to only 'acss_debit' hid US/UK bank accounts from the
// "Pay with" list entirely.
const BANK_DEBIT_PAYMENT_TYPES = ['acss_debit', 'us_bank_account', 'bacs_debit'];

// PaymentElement's own `options` only accepts things like `fields`/`layout`/
// `defaultValues` - `clientSecret` belongs solely on <Elements>. Passing the
// same object with `clientSecret` to both was silently rejected by Stripe.js
// ("clientSecret is not a recognized parameter") and left the bank-account
// fields collapsed to a near-zero-height, empty iframe.
const paymentElementOptions = {
  fields: {
    billingDetails: {
      name: 'never',
      email: 'never',
    },
  },
};

const EftFormModal = ({
  show,
  onClose,
  cateringOrder,
  clearCateringOrder,
  orderHandler,
  selectedStripePaymentMethod
}) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const [active, setActive] = useState(null);
  const [selectedCard, setSelectedCard] = useState(null);
  const [saveCard, setSaveCard] = useState(false);

  useEffect(() => {
    const channel = new BroadcastChannel('card_channel');
    channel.onmessage = event => {
      const eventCard = event.data;
      dispatch({
        type: UPDATE_SAVED_CARD_IN_CATEGORING_ORDER,
        data: eventCard,
      });
    };
    return () => {
      channel.close();
    };
  }, []);

  const defaultSelectedCard = useMemo(
    () => cateringOrder?.savedCards?.find(item => (item?.isDefault || (selectedStripePaymentMethod == item?.paymentMethodId)) && BANK_DEBIT_PAYMENT_TYPES.includes(item.paymentType)),
    [cateringOrder?.savedCards, selectedStripePaymentMethod],
  );

  const savedCardOptions = useMemo(
    () =>
      cateringOrder?.savedCards
        ?.filter(item => BANK_DEBIT_PAYMENT_TYPES.includes(item.paymentType))
        ?.map(item => ({
          label: item.name,
          value: item.paymentMethodId,
        })),
    [cateringOrder?.savedCards],
  );

  useEffect(() => {
    setSelectedCard(
      defaultSelectedCard
        ? {
          label: defaultSelectedCard?.name,
          value: defaultSelectedCard?.paymentMethodId,
        }
        : null,
    );
    if (defaultSelectedCard) {
      setActive(defaultSelectedCard?.paymentMethodId);
    } else if (cateringOrder && !cateringOrder?.savedCards?.length) {
      setActive('add');
    } else {
      setActive(null);
      setSelectedCard(null);
    }
  }, [cateringOrder, defaultSelectedCard, cateringOrder?.savedCards]);

  const elementsOptions = {
    clientSecret: cateringOrder?.clientSecret,
  };

  const userName = cateringOrder?.fullName;
  const userEmail = cateringOrder?.email;

  const handleOnSubmitExistingCard = () => {
    if (active && cateringOrder.purchaseId !== 'add') {
      orderHandler(active);
    }
  };

  const RenderPaymentForm = () => {
    const stripe = useStripe();
    const elements = useElements();
    const [isLoading, setIsLoading] = useState(false);
    const onSuccess = () => {
      history.push('/checkout-completed');
    };

    const handleSubmit = async () => {
      if (!stripe || !elements) {
        return;
      }
      setIsLoading(true);
      // confirmSetup() is payment-method-agnostic - unlike the old
      // confirmAcssDebitSetup(), it works for whichever bank-debit method
      // PaymentElement actually rendered for this customer (ACSS Debit for
      // Canada, ACH for the US, Bacs Debit for the UK, ...).
      const { setupIntent, error } = await stripe.confirmSetup({
        elements,
        confirmParams: {
          payment_method_data: {
            billing_details: { name: userName, email: userEmail },
          },
          return_url: window.location.href,
        },
        redirect: 'if_required',
      });

      if (error) {
        setIsLoading(false);
        onClose();
        store.addNotification({
          message: error.message,
          type: 'danger',
          insert: 'top',
          container: 'top-right',
          animationIn: ['animated', 'bounceIn'],
          animationOut: ['animated', 'fadeOut'],
          dismiss: {
            duration: 8000,
          },
        });
        return;
      }
      if (setupIntent.status === 'succeeded') {
        try {
          const paymentMethodId = setupIntent.payment_method;
          const purchaseId = cateringOrder?.purchaseId;
          await finalizingCateringOrderAction(
            paymentMethodId,
            purchaseId,
            saveCard,
            onSuccess,
            setIsLoading,
          );
          await clearCateringOrder();
        } catch (error) {
          onClose();
          store.addNotification({
            message: error,
            type: 'danger',
            insert: 'top',
            container: 'top-right',
            animationIn: ['animated', 'bounceIn'],
            animationOut: ['animated', 'fadeOut'],
            dismiss: {
              duration: 8000,
            },
          });
        }
      }
    };

    return (
      <>
        {isLoading ?
          <LoadingAnimation /> :
          null
        }
        <div className="space-y-4">
          <div>
            <label
              htmlFor="Field-nameInput"
              className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5"
            >
              Full Name
            </label>
            <input
              id="Field-nameInput"
              className="w-full h-[46px] px-3.5 rounded-xl border border-gray-200 bg-gray-50 text-sm text-[#202124] disabled:opacity-70"
              type="text"
              name="full_name"
              required="required"
              value={userName}
              disabled
            />
          </div>
          <div>
            <label
              htmlFor="Field-emailInput"
              className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-1.5"
            >
              Email
            </label>
            <input
              id="Field-emailInput"
              className="w-full h-[46px] px-3.5 rounded-xl border border-gray-200 bg-gray-50 text-sm text-[#202124] disabled:opacity-70"
              type="email"
              name="email"
              value={userEmail}
              required="required"
              disabled
            />
          </div>

          <label
            htmlFor="saveCard"
            className="flex items-start gap-2.5 text-sm text-gray-500 cursor-pointer"
          >
            <input
              className="mt-0.5 w-4 h-4 rounded accent-panda-red flex-shrink-0"
              type="checkbox"
              id="saveCard"
              checked={saveCard}
              onChange={() => setSaveCard(!saveCard)}
            />
            <span>
              By checking this box, you agree to save your bank account for
              later. This will speed up your checkout process.
            </span>
          </label>
        </div>

        <div className="mt-4">
          <PaymentElement options={paymentElementOptions} />
        </div>
        <button
          onClick={handleSubmit}
          disabled={isLoading || !stripe}
          className="mt-5 w-full py-3.5 px-4 rounded-xl bg-gradient-to-r from-panda-red to-panda-dark hover:from-panda-dark hover:to-panda-dark text-white font-heading font-bold text-sm shadow-md shadow-panda-red/30 active:scale-98 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
        >
          Confirm and Proceed to Payment
        </button>
      </>
    );
  };

  if (!show) return null;

  return (
    <div
      className="fixed inset-0 z-50 overflow-y-auto bg-[#202124]/70 backdrop-blur-xs flex items-center justify-center p-3 sm:p-4"
      onClick={onClose}
    >
      <div
        className="relative bg-white rounded-2xl shadow-2xl max-w-lg w-full border border-panda-tint p-6 sm:p-7 max-h-[85vh] overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="absolute right-3 top-3 w-8 h-8 rounded-full bg-gray-100 text-gray-500 hover:bg-[#E53935] hover:text-white flex items-center justify-center transition-colors"
        >
          <X className="w-4 h-4" />
        </button>
        <h3 className="font-heading font-bold text-lg text-[#202124] pr-6">
          Verify Your Details
        </h3>
        <div className="mt-5">
          {cateringOrder?.savedCards?.length ? (
            <FormSelect
              name="numPeopleRange"
              placeholder="Pay with"
              className="mb-5"
              value={selectedCard}
              options={[
                ...savedCardOptions,
                { label: 'Add new bank account', value: 'add' },
              ]}
              onChange={(_, value, card) => {
                setActive(value);
                setSelectedCard(card);
              }}
            />
          ) : null}
          {cateringOrder && cateringOrder.clientSecret ? (
            <Elements stripe={getStripePromise()} options={elementsOptions}>
              {active === 'add' && <RenderPaymentForm />}
            </Elements>
          ) : (
            <LoadingAnimation />
          )}
          {active !== 'add' && (
            <button
              onClick={handleOnSubmitExistingCard}
              disabled={!active}
              className="mt-5 w-full py-3.5 px-4 rounded-xl bg-gradient-to-r from-panda-red to-panda-dark hover:from-panda-dark hover:to-panda-dark text-white font-heading font-bold text-sm shadow-md shadow-panda-red/30 active:scale-98 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Confirm and Proceed to Payment
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

EftFormModal.propTypes = {
  show: PropTypes.bool,
  onClose: PropTypes.func,
  orderHandler: PropTypes.func,
  loadingHandler: PropTypes.func,
  setSaveCard: PropTypes.func,
  totalPrice: PropTypes.number.isRequired,
  clearCateringOrder: PropTypes.func.isRequired,
  cateringOrder: PropTypes.shape({
    savedCards: PropTypes.arrayOf(
      PropTypes.objectOf({
        name: PropTypes.string,
        payment_method_id: PropTypes.string,
      }),
    ),
    clientSecret: PropTypes.string,
    purchaseId: PropTypes.number.isRequired,
  }).isRequired,
};

EftFormModal.defaultProps = {
  show: true,
  onClose: () => { },
  orderHandler: () => { },
  loadingHandler: () => { },
  setSaveCard: () => { },
};

export default EftFormModal;
